/**
 * The roles, assigned by SWA invitation (Elijah assigns them; the app never grants one). 1.5.0 adds `tech`: the
 * FORA technology support associate — Today with their own tickets and Help & Support, nothing else.
 */
export const ROLES = ['admin', 'coadmin', 'scheduler', 'crew', 'tech', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

/** Everyone who works the housekeeping day (every role but the tech, who sees only their tickets). */
export const HOUSE_ROLES = ['admin', 'coadmin', 'scheduler', 'crew', 'viewer'] as const satisfies readonly Role[];

/** Who reaches what. Mirrors staticwebapp.config.json route rules; the API enforces the same with withAuth. */
export const ACCESS = {
  everyone: ROLES,
  /** Today, the Day plan: the housekeeping day (not the tech — 1.5.0). */
  house: HOUSE_ROLES,
  /** Tech Support tickets (1.5.0): the associate, and the office that follows them. 26 Oct 2026: not the scheduler (D-1020-SMALL: no /tech and no Tech booking). */
  tech: ['admin', 'coadmin', 'tech'] as const,
  /** Book → Tech Support (1.5.0). 1.8.5 (D-1.8.5-0a): the tech books his own tickets too. */
  techBook: ['admin', 'coadmin', 'tech'] as const,
  /** 1.8.5: finding a tenant to book — the schedule roles and the tech (who sees no phone or email in the answer). */
  tenantPick: ['admin', 'coadmin', 'scheduler', 'viewer', 'tech'] as const,
  /** 1.8.5 (D-1.8.5-1): the Tech Support tab — the office and the tech (the viewer is Housekeeping only since 20 Oct 2026). */
  techTab: ['admin', 'coadmin', 'tech'] as const,
  /** 1.8.5 (D-1.8.5-0a): who makes and invites to a workshop — the office, and the tech for his own. */
  workshops: ['admin', 'coadmin', 'tech'] as const,
  staff: ['admin', 'coadmin'] as const,
  /** An event's page (1.7.0): the office works it; the scheduler reads it. */
  eventRead: ['admin', 'coadmin', 'scheduler'] as const,
  admin: ['admin'] as const,
  /** Housekeeping schedule (Look up, Book, Planning): not the crew. */
  schedule: ['admin', 'coadmin', 'scheduler', 'viewer'] as const,
} satisfies Record<string, readonly Role[]>;

export function hasAnyRole(userRoles: readonly string[] | undefined, allowed: readonly Role[]): boolean {
  if (!userRoles) return false;
  return allowed.some((r) => userRoles.includes(r));
}

/** The app's own role for a principal: the strongest one it carries. */
export function primaryRole(userRoles: readonly string[] | undefined): Role | null {
  for (const r of ROLES) if (userRoles?.includes(r)) return r;
  return null;
}

/* ---------------- 1.8.0 (D-1.8.0-1): what each of the five roles can do, said once ---------------- */

/** The five roles the office works with (the tech is the sixth, with only their tickets). */
export const FIVE_ROLES = ['admin', 'coadmin', 'scheduler', 'crew', 'viewer'] as const satisfies readonly Role[];
export type FiveRole = (typeof FIVE_ROLES)[number];

export interface RoleCan {
  /** The role's name as the assistant says it ("administrator"). */
  name: { en: string; es: string };
  /** The addresses the role may open — src/app/nav.ts ROUTE_ROLES, held to it by src/app/roles-can-180.test.ts. */
  routes: readonly string[];
  /** What the role can do, a sentence a line, in both languages. */
  can: readonly { en: string; es: string }[];
}

const L = (en: string, es: string) => ({ en, es });
const SCHEDULE_ROUTES = ['/', '/housekeeping', '/lookup', '/schedule', '/planning', '/planning/numbers', '/calendar', '/people/:tenantId', '/visits/:appointmentId', '/settings', '/help'] as const;

/**
 * Listed by hand once (the role gates are spread through staticwebapp.config.json and the code); the test holds
 * `routes` to ROUTE_ROLES both ways, so a route given to a role or taken from it fails until this list says so.
 */
export const ROLE_CAN: Readonly<Record<FiveRole, RoleCan>> = {
  admin: {
    name: L('administrator', 'administrador'),
    routes: [...SCHEDULE_ROUTES, '/companion', '/reading', '/tech', '/tech/:appointmentId', '/messages', '/people', '/people/:tenantId/home-visit', '/volunteers', '/volunteers/:volunteerId', '/events', '/events/:eventId', '/events/:eventId/roster', '/events/surveys', '/events/surveys/:slug', '/events/month', '/events/announcements', '/events/announcements/:id', '/documents', '/reports', '/admin'],
    can: [
      L('Everything: the housekeeping day, Book, Planning, the Outbox, People, Volunteers, Events.', 'Todo: el día de limpieza, Agendar, Planificación, la Bandeja de salida, Personas, Voluntarios, Eventos.'),
      L('The Admin portal under the gear: Users, Settings, Email templates, Service days, Fix it, Review, Audit, Health, Import, Export.', 'El portal de Admin bajo el engranaje: Usuarios, Configuración, Plantillas de correo, Días de servicio, Arreglar, Revisión, Auditoría, Estado, Importar, Exportar.'),
      L('Skips or overrides that the app reserves for the administrator (Fix it, the settings, promoting a typed organization).', 'Los atajos que la aplicación reserva al administrador (Arreglar, la configuración, agregar una organización escrita a la lista).'),
    ],
  },
  coadmin: {
    name: L('co-administrator', 'coadministrador'),
    routes: [...SCHEDULE_ROUTES, '/companion', '/reading', '/tech', '/tech/:appointmentId', '/messages', '/people', '/people/:tenantId/home-visit', '/volunteers', '/volunteers/:volunteerId', '/events', '/events/:eventId', '/events/:eventId/roster', '/events/surveys', '/events/surveys/:slug', '/events/month', '/events/announcements', '/events/announcements/:id', '/documents', '/reports'],
    can: [
      L('The housekeeping day, Book, Planning and the Outbox.', 'El día de limpieza, Agendar, Planificación y la Bandeja de salida.'),
      L('People (sign-ups, home visits, feedback on a tenant’s behalf, incident memos), Volunteers and Events.', 'Personas (inscripciones, visitas al hogar, comentarios en nombre de un inquilino, memos de incidentes), Voluntarios y Eventos.'),
      L('Not the Admin portal: Users and Settings are the administrator’s.', 'No el portal de Admin: Usuarios y Configuración son del administrador.'),
    ],
  },
  scheduler: {
    name: L('scheduler', 'programador'),
    routes: [...SCHEDULE_ROUTES, '/events/:eventId'],
    can: [
      L('The Housekeeping schedule: Today, Look up, Book and Planning.', 'La agenda de Limpieza: Hoy, Buscar, Agendar y Planificación.'),
      L('Reads an event’s page; does not make or send anything for Events.', 'Lee la página de un evento; no crea ni envía nada de Eventos.'),
      L('Not Messages, People, Volunteers, the Events list, Surveys, Announcements or the Admin portal.', 'No Mensajes, Personas, Voluntarios, la lista de Eventos, las Encuestas, los Anuncios ni el portal de Admin.'),
    ],
  },
  crew: {
    name: L('crew', 'equipo'),
    routes: ['/', '/visits/:appointmentId', '/settings', '/help'],
    can: [
      L('Today with their own visits: Call and Finish (the close-out is filled in for them).', 'Hoy con sus propias visitas: Llamar y Terminar (el cierre viene rellenado).'),
      L('Reads their own crew’s visits; Settings (the gear) and Help & Support.', 'Lee las visitas de su propio equipo; Configuración (el engranaje) y Ayuda y soporte.'),
      L('Does not book, plan, send or see People; the assistant is not offered to the crew.', 'No agenda, no planifica, no envía ni ve Personas; el asistente no se ofrece al equipo.'),
    ],
  },
  viewer: {
    name: L('viewer', 'observador'),
    routes: [...SCHEDULE_ROUTES],
    can: [
      L('Reads the housekeeping day, Look up, the Schedule, Planning and the Calendar.', 'Lee el día de limpieza, Buscar, la Agenda, Planificación y el Calendario.'),
      L('Does not book, send, approve or change anything.', 'No agenda, no envía, no aprueba ni cambia nada.'),
    ],
  },
};

/** "administrator" — the role's name in the asker's language; the strongest of several. */
export function roleName(userRoles: readonly string[] | undefined, lang: 'en' | 'es'): string {
  const r = FIVE_ROLES.find((x) => userRoles?.includes(x));
  return r ? ROLE_CAN[r].name[lang] : lang === 'es' ? 'sin rol' : 'no role';
}
